import { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';

import Remittance, {
    PreRemittanceState
} from '../../routes/Financial/generateDocumentsForms/Remittance/Remittance';
import Receipt, {
    PreReceiptState
} from '../../routes/Financial/generateDocumentsForms/Receipt/Receipt';
import {
    CARD_DOCUMENT_TYPE_KEY,
    CARD_REMITTANCE_FORMER_PAGE_KEY,
    CardDocumentType,
    PENDING_CARD_REMITTANCE_KEY
} from '../../routes/Financial/generateDocumentsForms/subForms/CreditCardSubForm';
import { checkTokenAndApprovedDeal, getVoucherDetails } from '../InnerPaymentResult/api';
import { displayToast } from '../RTToast';
import { Voucher } from '../../api/vouchers/types';

// Verify the charge, retrying a few times. The container's DNS to the Pelecard
// gateway is occasionally flaky (EAI_AGAIN), which makes a single call 500 even
// though the card went through — a retry almost always clears it. Returns the
// deal details on a definitive answer, or null when every attempt errored
// (network/verification failure, distinct from a declined card).
const verifyChargeWithRetry = async (
    transactionId: string,
    /** The terminal the charge settled on. Pelecard scopes a transaction to its
     *  terminal, so verifying without it asks the DEFAULT row and finds nothing
     *  for a charge taken on any other terminal. */
    paymentsTerminalId?: string | null,
    attempts = 4
): Promise<any | null> => {
    for (let i = 0; i < attempts; i++) {
        const dealDetails = await checkTokenAndApprovedDeal(transactionId, paymentsTerminalId);
        // A definitive gateway answer carries a StatusCode (approved or declined).
        if (dealDetails && dealDetails.StatusCode !== undefined) return dealDetails;
        // Otherwise the call itself failed (checkTokenAndApprovedDeal swallows
        // the error and returns undefined) — wait and try again.
        if (i < attempts - 1) await new Promise((r) => setTimeout(r, 1500));
    }
    return null;
};

/**
 * Issues a Remittance or Receipt after a Masleka (Pelecard) credit-card charge.
 *
 * The card flow can't finish in place: Pelecard returns by redirecting the whole
 * window back to the former page, which reloads the app and discards the open
 * dialog and its form state. Before opening the payment iframe the card sub-form
 * persists everything needed — the form values + source document in Redis, and
 * the session/transaction ids + a pending flag + the document type in
 * localStorage.
 *
 * Mounted once at the app root (inside all api providers) so it runs on whatever
 * page the gateway lands on, regardless of which screen opened the dialog. On
 * finding the pending flag it verifies the charge, reloads the payload, and
 * renders a hidden Remittance or Receipt in autoIssue mode — which creates the
 * document and pops its PDF, exactly like the non-card payment methods do.
 */
const CardRemittanceResume = () => {
    const [resume, setResume] = useState<{
        documentType: CardDocumentType;
        values: PreRemittanceState | PreReceiptState;
        documentGenerationData: Partial<Voucher> | null;
        selectedDocuments: Array<Voucher> | null;
    } | null>(null);

    // Guards against React StrictMode's double effect invocation issuing twice
    // within the same mount. The localStorage flag guards across reloads.
    const startedRef = useRef(false);

    const history = useHistory();

    useEffect(() => {
        if (startedRef.current) return;
        if (localStorage.getItem(PENDING_CARD_REMITTANCE_KEY) !== '1') return;
        startedRef.current = true;

        console.info('[CardRemittance] pending flag found — resuming', {
            documentType: localStorage.getItem(CARD_DOCUMENT_TYPE_KEY),
            landedOn: `${window.location.pathname}${window.location.search}`,
            formerPage: localStorage.getItem(CARD_REMITTANCE_FORMER_PAGE_KEY),
            creditSessionId: localStorage.getItem('creditSessionId'),
            creditTransactionId: localStorage.getItem('creditTransactionId')
        });

        // Pelecard does not reliably return to the deep GoodURL we pass — it lands
        // the window on the app root. Navigate back to the page the user was on
        // before the payment ourselves, so issuing the document (and popping its
        // PDF) happens in the right place. In-app SPA navigation, so no reload.
        const formerPage = localStorage.getItem(CARD_REMITTANCE_FORMER_PAGE_KEY);
        if (formerPage) {
            const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
            if (formerPage !== current) {
                console.info('[CardRemittance] navigating back to former page', formerPage);
                history.replace(formerPage);
            }
        }

        const redisId = localStorage.getItem('creditSessionId');
        const transactionId = localStorage.getItem('creditTransactionId');
        if (!redisId || !transactionId) {
            console.warn('[CardRemittance] missing redisId/transactionId — aborting', {
                redisId,
                transactionId
            });
            localStorage.removeItem(PENDING_CARD_REMITTANCE_KEY);
            localStorage.removeItem(CARD_REMITTANCE_FORMER_PAGE_KEY);
            localStorage.removeItem(CARD_DOCUMENT_TYPE_KEY);
            return;
        }

        // Clears the pending flag + one-shot payment keys. Called only on a
        // DEFINITIVE result (approved or declined) — not on a transient network
        // failure, so a refresh can still recover a charge we couldn't verify.
        const clearPending = () => {
            localStorage.removeItem(PENDING_CARD_REMITTANCE_KEY);
            localStorage.removeItem(CARD_REMITTANCE_FORMER_PAGE_KEY);
            localStorage.removeItem(CARD_DOCUMENT_TYPE_KEY);
            localStorage.removeItem('creditSessionId');
            localStorage.removeItem('creditTransactionId');
            localStorage.removeItem('creditUserKey');
            localStorage.removeItem('creditTotalInCents');
        };

        const finalize = async () => {
            try {
                const dealDetails = await verifyChargeWithRetry(
                    transactionId,
                    localStorage.getItem('creditPaymentsTerminalId')
                );
                console.info('[CardRemittance] verify result', dealDetails);

                // Every verification attempt errored (e.g. DNS EAI_AGAIN to the
                // gateway). The card may well have been charged, so DON'T discard
                // the pending state — a refresh will retry. Also allow this mount
                // to retry.
                if (!dealDetails) {
                    startedRef.current = false;
                    displayToast(
                        'error',
                        'Could not verify payment',
                        'The gateway could not be reached. Please refresh the page to try again.'
                    );
                    return;
                }

                // StatusCode '000' = approved (Pelecard). Anything else is a
                // genuine decline — that is final, so clear the pending state.
                if (dealDetails.StatusCode !== '000') {
                    clearPending();
                    displayToast(
                        'error',
                        'Payment failed',
                        dealDetails?.ErrorMessage || 'The card charge was not approved.'
                    );
                    return;
                }

                const voucherDetails = await getVoucherDetails(redisId);
                console.info('[CardRemittance] redis payload', voucherDetails);
                if (!voucherDetails) {
                    console.warn('[CardRemittance] no redis payload — aborting');
                    clearPending();
                    return;
                }
                const parsed =
                    typeof voucherDetails === 'string' ? JSON.parse(voucherDetails) : voucherDetails;
                const { values, documentGenerationData, selectedDocuments } = parsed || {};
                if (!values) {
                    console.warn('[CardRemittance] redis payload has no values — aborting', parsed);
                    clearPending();
                    return;
                }
                console.info('[CardRemittance] rendering hidden document to auto-issue');
                const documentType =
                    (localStorage.getItem(CARD_DOCUMENT_TYPE_KEY) as CardDocumentType) || 'REMITTANCE';
                setResume({
                    documentType,
                    values,
                    documentGenerationData: documentGenerationData ?? null,
                    selectedDocuments: selectedDocuments ?? null
                });
                clearPending();
            } catch (err) {
                console.error('Failed to finalize card remittance', err);
                startedRef.current = false;
            }
        };
        finalize();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    if (!resume) return null;

    if (resume.documentType === 'RECEIPT') {
        return (
            <Receipt
                open={false}
                toggleOpen={() => setResume(null)}
                documentGenerationData={resume.documentGenerationData}
                autoIssueValues={resume.values as PreReceiptState}
            />
        );
    }

    return (
        <Remittance
            open={false}
            toggleOpen={() => setResume(null)}
            documentGenerationData={resume.documentGenerationData}
            selectedDocuments={resume.selectedDocuments}
            autoIssueValues={resume.values as PreRemittanceState}
        />
    );
};

export default CardRemittanceResume;
